import { useState, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { standupAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  FileText, Download, Mail, Loader2, CheckCircle2, Clock,
  AlertTriangle, ArrowRight, Sparkles, Users, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function WeeklyStandupPage({ embedded = false }) {
  const { currentWorkspace } = useWorkspace();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const wsId = currentWorkspace?.id;

  const generate = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const res = await standupAPI.generate(wsId);
      setReport(res.data);
    } catch { toast.error('Failed to generate report'); }
    setLoading(false);
  }, [wsId]);

  const downloadPdf = async () => {
    if (!wsId) return;
    setPdfLoading(true);
    try {
      const res = await standupAPI.downloadPdf(wsId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly_standup_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download'); }
    setPdfLoading(false);
  };

  const sendEmail = async () => {
    if (!wsId) return;
    setEmailLoading(true);
    try {
      const res = await standupAPI.sendEmail(wsId);
      toast.success(`Standup sent to ${res.data.count} members`);
    } catch { toast.error('Failed to send'); }
    setEmailLoading(false);
  };

  return (
    <div className={embedded ? 'space-y-6' : 'max-w-4xl mx-auto p-4 md:p-8'} data-testid="weekly-standup-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          {!embedded && <h1 className="text-xl font-display font-bold">Weekly Team Standup</h1>}
          <p className="text-[12px] text-muted-foreground/50 mt-1">AI-generated summary of each member's weekly accomplishments, blockers, and goals</p>
        </div>
        <div className="flex items-center gap-2">
          {report && (
            <>
              <Button variant="outline" size="sm" className="h-8 text-[11px] border-border/30 gap-1.5" onClick={downloadPdf} disabled={pdfLoading} data-testid="standup-pdf-btn">
                {pdfLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} PDF
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-[11px] border-border/30 gap-1.5" onClick={sendEmail} disabled={emailLoading} data-testid="standup-email-btn">
                {emailLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Email Team
              </Button>
            </>
          )}
          <Button size="sm" className="h-8 text-[11px] bg-gold/10 hover:bg-gold/20 text-gold border-0 gap-1.5" onClick={generate} disabled={loading} data-testid="generate-standup-btn">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {report ? 'Refresh' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {!report && !loading && (
        <div className="text-center py-20">
          <Users className="h-12 w-12 text-muted-foreground/10 mx-auto mb-4" />
          <p className="text-[14px] text-muted-foreground/30 font-medium">Generate your weekly standup report</p>
          <p className="text-[12px] text-muted-foreground/20 mt-2 max-w-sm mx-auto">The AI will compile each team member's accomplishments, blockers, and next-week goals into a shareable report.</p>
          <Button size="sm" className="mt-6 bg-gold/10 hover:bg-gold/20 text-gold border-0 text-[12px]" onClick={generate}>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate Report
          </Button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gold/40 mx-auto mb-4" />
          <p className="text-[13px] text-muted-foreground/40">Analyzing team performance and generating insights...</p>
        </div>
      )}

      {report && !loading && (
        <div className="space-y-6">
          {/* Stats bar */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Completed', value: report.totals.completed, color: 'text-green-500' },
              { label: 'In Progress', value: report.totals.in_progress, color: 'text-gold' },
              { label: 'Overdue', value: report.totals.overdue, color: 'text-red-400' },
              { label: 'Hours Logged', value: `${report.totals.hours}h`, color: 'text-foreground' },
            ].map(s => (
              <Card key={s.label} className="bg-card/60 border-border/20">
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-display font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-muted-foreground/40 mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* AI Executive Summary */}
          {report.ai_summary && (
            <Card className="bg-card/60 border-gold/10">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-gold/60" />
                  <span className="text-[13px] font-semibold">AI Executive Summary</span>
                </div>
                <div className="text-[12.5px] leading-relaxed text-muted-foreground whitespace-pre-wrap">{report.ai_summary}</div>
              </CardContent>
            </Card>
          )}

          <Separator className="opacity-30" />

          {/* Member reports */}
          <div>
            <h2 className="text-[14px] font-semibold mb-3">Team Member Reports</h2>
            <div className="space-y-3">
              {report.members.map(mr => (
                <Card key={mr.user_id} className="bg-card/60 border-border/20" data-testid={`member-report-${mr.user_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-gold/10 flex items-center justify-center">
                          <span className="text-[11px] font-semibold text-gold">{(mr.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-[13px] font-medium">{mr.name}</p>
                          <p className="text-[10px] text-muted-foreground/30">{mr.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px] border-border/20">{mr.hours_logged}h logged</Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Completed */}
                      {mr.completed.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-green-500/70 flex items-center gap-1 mb-1.5"><CheckCircle2 className="h-3 w-3" /> Completed ({mr.completed_count})</p>
                          {mr.completed.slice(0, 4).map((t, i) => (
                            <p key={i} className="text-[11px] text-muted-foreground/50 py-0.5 truncate">- {t.title}</p>
                          ))}
                        </div>
                      )}
                      {/* In Progress */}
                      {mr.in_progress.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-gold/70 flex items-center gap-1 mb-1.5"><Clock className="h-3 w-3" /> In Progress ({mr.in_progress_count})</p>
                          {mr.in_progress.slice(0, 4).map((t, i) => (
                            <p key={i} className="text-[11px] text-muted-foreground/50 py-0.5 truncate">- {t.title}</p>
                          ))}
                        </div>
                      )}
                      {/* Overdue / Blockers */}
                      {mr.overdue.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-red-400/70 flex items-center gap-1 mb-1.5"><AlertTriangle className="h-3 w-3" /> Blockers ({mr.overdue_count})</p>
                          {mr.overdue.slice(0, 3).map((t, i) => (
                            <p key={i} className="text-[11px] text-red-400/40 py-0.5 truncate">- {t.title}</p>
                          ))}
                        </div>
                      )}
                      {/* Upcoming */}
                      {mr.upcoming.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-blue-400/70 flex items-center gap-1 mb-1.5"><ArrowRight className="h-3 w-3" /> Next Week ({mr.upcoming.length})</p>
                          {mr.upcoming.slice(0, 4).map((t, i) => (
                            <p key={i} className="text-[11px] text-muted-foreground/50 py-0.5 truncate">- {t.title}</p>
                          ))}
                        </div>
                      )}
                    </div>

                    {mr.completed.length === 0 && mr.in_progress.length === 0 && mr.overdue.length === 0 && (
                      <p className="text-[11px] text-muted-foreground/30 text-center py-3">No task activity this week</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
